const ESC = 0x1b;
const GS = 0x1d;
const LF = 0x0a;

let cachedDevice = null;
let disconnectListenerReady = false;

const ensureDisconnectListener = () => {
  if (disconnectListenerReady || typeof navigator === "undefined") {
    return;
  }
  if (!navigator.usb) {
    return;
  }
  disconnectListenerReady = true;
  navigator.usb.addEventListener("disconnect", (event) => {
    if (cachedDevice && event.device === cachedDevice) {
      cachedDevice = null;
    }
  });
};

const isWebUsbSupported = () =>
  typeof navigator !== "undefined" && !!navigator.usb;

const sanitizeText = (value) => {
  if (value === null || value === undefined) {
    return "";
  }
  return value
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\x00-\x7f]/g, "");
};

const textEncoder = new TextEncoder();

const encodeText = (text) => textEncoder.encode(sanitizeText(text));

const buildFilters = ({ vendorId, productId } = {}) => {
  const filters = [];
  if (vendorId && productId) {
    filters.push({ vendorId, productId });
  } else if (vendorId) {
    filters.push({ vendorId });
  } else {
    filters.push({ classCode: 0x07 });
  }
  return filters;
};

const requestUsbDevice = async ({ vendorId, productId } = {}) => {
  if (!isWebUsbSupported()) {
    throw new Error("WEBUSB_NOT_SUPPORTED");
  }
  ensureDisconnectListener();
  const filters = buildFilters({ vendorId, productId });
  return navigator.usb.requestDevice({ filters });
};

export const ensureUsbPrinter = async ({ vendorId, productId } = {}) => {
  if (!isWebUsbSupported()) {
    throw new Error("WEBUSB_NOT_SUPPORTED");
  }
  ensureDisconnectListener();
  if (cachedDevice) {
    return cachedDevice;
  }
  const device = await requestUsbDevice({ vendorId, productId });
  cachedDevice = device;
  return device;
};

const findUsbInterface = (device) => {
  const configuration = device.configuration;
  if (!configuration) {
    return null;
  }
  for (const iface of configuration.interfaces) {
    for (const alternate of iface.alternates) {
      const outEndpoint = alternate.endpoints.find(
        (endpoint) => endpoint.direction === "out"
      );
      if (outEndpoint) {
        return {
          interfaceNumber: iface.interfaceNumber,
          alternateSetting: alternate.alternateSetting,
          endpointNumber: outEndpoint.endpointNumber,
        };
      }
    }
  }
  return null;
};

const openUsbPrinter = async (device) => {
  if (!device.opened) {
    await device.open();
  }

  if (!device.configuration) {
    await device.selectConfiguration(1);
  }

  const iface = findUsbInterface(device);
  if (!iface) {
    throw new Error("USB_INTERFACE_NOT_FOUND");
  }

  await device.claimInterface(iface.interfaceNumber);
  if (iface.alternateSetting !== undefined) {
    await device.selectAlternateInterface(
      iface.interfaceNumber,
      iface.alternateSetting
    );
  }

  return iface;
};

const transferInChunks = async (device, endpointNumber, data) => {
  const chunkSize = 512;
  for (let offset = 0; offset < data.length; offset += chunkSize) {
    const chunk = data.slice(offset, offset + chunkSize);
    await device.transferOut(endpointNumber, chunk);
  }
};

const createWriter = () => {
  const bytes = [];
  const push = (...values) => {
    values.forEach((value) => {
      if (Array.isArray(value)) {
        bytes.push(...value);
      } else if (value instanceof Uint8Array) {
        bytes.push(...value);
      } else if (typeof value === "number") {
        bytes.push(value);
      }
    });
  };
  const newline = () => push(LF);
  const textLine = (text) => {
    if (text) {
      push(encodeText(text));
    }
    newline();
  };
  const align = (mode) => {
    const alignMap = { left: 0, center: 1, right: 2 };
    push(ESC, 0x61, alignMap[mode] ?? 0);
  };
  const bold = (enabled) => {
    push(ESC, 0x45, enabled ? 1 : 0);
  };
  const cut = () => {
    push(GS, 0x56, 0x00);
  };
  const initialize = () => {
    push(ESC, 0x40);
    push(ESC, 0x74, 0x10);
  };
  return {
    bytes,
    push,
    newline,
    textLine,
    align,
    bold,
    cut,
    initialize,
  };
};

const buildTicketBytes = (order) => {
  const writer = createWriter();
  const orderData = order || {};
  const userData = orderData.usuario || {};

  const totalConImpuestos = Number(orderData.totalConImpuestos || 0);
  const totalDeTransporte = Number(orderData.totalDeTransporte || 0);
  const subTotal = (totalConImpuestos * 100) / 113;
  const iva = totalConImpuestos - subTotal;
  const ivaTransporte =
    totalDeTransporte > 0 ? totalDeTransporte - (totalDeTransporte * 100) / 113 : 0;
  const totalConTransporte = Number(orderData.totalConTransporte || totalConImpuestos);

  writer.initialize();
  writer.align("center");
  writer.bold(true);
  writer.textLine("TiColitas S.R.L.");
  writer.bold(false);
  writer.textLine("3-102-832693");
  writer.textLine("");
  writer.align("left");
  writer.textLine("Tel: 2451-8181");
  writer.textLine("WhatsApp: 8313-3113");
  writer.textLine("www.ticolitas.com");
  writer.textLine(new Date().toLocaleString());
  writer.textLine("");
  writer.align("center");
  writer.textLine(`Orden ${orderData.id ?? ""}`);
  writer.textLine("");

  (orderData.lineas || []).forEach((linea) => {
    const producto = linea.producto || {};
    writer.align("left");
    writer.textLine(
      `${producto.id || ""} - ${producto.marca || ""} ${(producto.nombreTienda || "").replace(/\//g, "")}`.trim()
    );
    writer.textLine(
      `Cant: ${Number(linea.cantidad || 0)} / Precio Uni: ${Number(
        linea.costoUnitario || 0
      ).toFixed(2)} / Desc: ${Number(linea.descuento || 0).toFixed(2)} / Total + IVA: ${Number(
        linea.totalConDescuento || 0
      ).toFixed(2)}`
    );
    writer.textLine("");
  });

  writer.align("right");
  writer.textLine(`Sub-Total ${subTotal.toFixed(2)}`);
  writer.textLine(`Descuento ${Number(orderData.descuentoGlobal || 0).toFixed(2)}`);
  writer.textLine(
    `Transporte ${(totalDeTransporte > 0 ? (totalDeTransporte * 100) / 113 : 0).toFixed(2)}`
  );
  writer.textLine(`I.V.A. ${(iva + ivaTransporte).toFixed(2)}`);
  writer.textLine(`Total ${totalConTransporte.toFixed(2)}`);
  writer.textLine("");
  writer.textLine("");
  writer.align("left");
  writer.textLine(
    "Autorizado mediante el oficio # DGT-R-033-2019 del 20 de 06 de 2019 de la D.G.T.D"
  );
  writer.textLine("");
  writer.textLine("");
  writer.textLine("");
  writer.textLine("");
  writer.textLine("");
  writer.textLine("");
  writer.textLine("");
  writer.textLine("");
  writer.cut();

  return new Uint8Array(writer.bytes);
};

const buildQrBytes = ({ text, url }) => {
  const writer = createWriter();
  const payload = sanitizeText(url || text || "");
  const qrData = encodeText(payload);
  const storeLength = qrData.length + 3;
  const pL = storeLength % 256;
  const pH = Math.floor(storeLength / 256);

  writer.initialize();
  writer.align("center");
  if (text) {
    writer.textLine(text);
  }

  writer.push(GS, 0x28, 0x6b, 0x04, 0x00, 0x31, 0x41, 0x32, 0x00);
  writer.push(GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x43, 0x06);
  writer.push(GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x45, 0x31);
  writer.push(GS, 0x28, 0x6b, pL, pH, 0x31, 0x50, 0x30, qrData);
  writer.push(GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x51, 0x30);

  writer.textLine("");
  writer.cut();

  return new Uint8Array(writer.bytes);
};

export const printTicketUsb = async (order, options = {}) => {
  const { device, vendorId, productId } = options;
  const selectedDevice = device || (await ensureUsbPrinter({ vendorId, productId }));
  const iface = await openUsbPrinter(selectedDevice);
  const payload = buildTicketBytes(order);
  await transferInChunks(selectedDevice, iface.endpointNumber, payload);
};

export const printQrUsb = async ({ text, url }, options = {}) => {
  const { device, vendorId, productId } = options;
  const selectedDevice = device || (await ensureUsbPrinter({ vendorId, productId }));
  const iface = await openUsbPrinter(selectedDevice);
  const payload = buildQrBytes({ text, url });
  await transferInChunks(selectedDevice, iface.endpointNumber, payload);
};

export const getUsbPrintErrorMessage = (error) => {
  if (!error) {
    return "No se pudo imprimir el tiquete.";
  }
  if (error.message === "WEBUSB_NOT_SUPPORTED") {
    return "Tu navegador no soporta impresion USB. Usa Chrome o Edge en Windows.";
  }
  if (error.message === "USB_INTERFACE_NOT_FOUND") {
    return "La impresora seleccionada no expone una interfaz USB compatible.";
  }
  if (error.name === "NotFoundError") {
    return "No se selecciono ninguna impresora.";
  }
  return "No se pudo imprimir el tiquete. Verifica la impresora y volve a intentar.";
};
